import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface BookieLeague {
  id: number;
  name: string;
  venue: string;
  start_date: string;
  end_date: string;
  squad_count: number;
  side_event_count: number;
  hidden: number;
}

const BookieLeagueList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [leagues, setLeagues] = useState<BookieLeague[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/bookie-league-management/leagues`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => setLeagues(res.data))
      .catch(() => setError(t('bookie.manageLeague.loadError', 'Failed to load leagues')))
      .finally(() => setLoading(false));
  }, [t]);

  const fmt = (d: string) => new Date(d).toLocaleDateString();

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/bookie/dashboard')}
            className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">
              {t('bookie.manageLeague.title', 'Manage League Side Action')}
            </h1>
            <p className="text-sm text-gray-400">
              {t('bookie.manageLeague.subtitle', 'Select a league to manage side events')}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/bookie/create-league')}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('bookie.manageLeague.newLeague', 'New League')}
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {error && (
        <div className="bg-red-900/40 border border-red-500 text-red-300 p-4 rounded-lg">
          {error}
        </div>
      )}

      {!loading && !error && leagues.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="text-5xl mb-4">🎳</div>
          <h3 className="text-xl font-semibold text-gray-300 mb-2">
            {t('bookie.manageLeague.noLeagues', 'No leagues yet')}
          </h3>
          <p className="text-gray-500 mb-6">
            {t('bookie.manageLeague.noLeaguesHint', 'Create your first side action league to get started')}
          </p>
          <button
            onClick={() => navigate('/bookie/create-league')}
            className="px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-colors"
          >
            {t('bookie.manageLeague.createFirst', 'Create First League')}
          </button>
        </div>
      )}

      {!loading && leagues.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {leagues.map(league => (
            <button
              key={league.id}
              onClick={() => navigate(`/bookie/manage-league/${league.id}`)}
              className="bg-gray-800 border border-gray-700 rounded-xl p-5 text-left hover:border-purple-500 hover:bg-gray-750 transition-all group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-lg text-white group-hover:text-purple-300 transition-colors line-clamp-1">
                      {league.name}
                    </h3>
                    {league.hidden === 1 && (
                      <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 bg-amber-900/60 border border-amber-600/50 text-amber-400 rounded-full">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                        Hidden
                      </span>
                    )}
                  </div>
                  <p className="text-gray-400 text-sm mt-0.5 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {league.venue}
                  </p>
                </div>
                <div className="w-8 h-8 bg-purple-900/40 rounded-lg flex items-center justify-center ml-2">
                  <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>

              <div className="text-xs text-gray-500 mb-3">
                {fmt(league.start_date)} → {fmt(league.end_date)}
              </div>

              <div className="flex items-center gap-3 text-sm">
                <span className="flex items-center gap-1 bg-gray-700/60 px-2.5 py-1 rounded-full text-gray-300">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {league.squad_count} {t('bookie.manageLeague.squads', 'squads')}
                </span>
                <span className="flex items-center gap-1 bg-purple-900/30 px-2.5 py-1 rounded-full text-purple-300">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {league.side_event_count} {t('bookie.manageLeague.sideEvents', 'side events')}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default BookieLeagueList;
